import React, { memo, useCallback, useMemo } from 'react';

import { Box, Button, Link, Paper, Typography } from '@mui/material';

import UpgradeToPremiumButton from '@/components/UpgradeToPremium/UpgradeToPremiumButton';
import useUserDetails from '@/hooks/useUserDetails';

import useJsonCsvPreview from './useJsonCsvPreview';

type JsonCsvPreviewScreenProps = {
  isCSV: boolean;
  exportCurlRequestString?: string;
  capturePosthogEvent: () => void;
  handlePrimaryButtonClick?: () => void;
};

const JsonCsvPreviewScreen = memo(
  ({
    isCSV,
    exportCurlRequestString,
    capturePosthogEvent,
    handlePrimaryButtonClick
  }: JsonCsvPreviewScreenProps) => {
    const { isUserPremium, isUserExpired } = useUserDetails();

    const { downloadFile, loading, error, fileContent } = useJsonCsvPreview(
      isCSV,
      capturePosthogEvent
    );

    const handleDownload = useCallback(() => {
      if (fileContent && !loading) {
        downloadFile();
      }
    }, [downloadFile, fileContent, loading]);

    const renderActionButtons = useMemo(() => {
      if (!isUserPremium || isUserExpired) {
        return (
          <UpgradeToPremiumButton
            sx={{ ml: 2 }}
            type='upgrade'
          />
        );
      }

      return (
        <Box sx={{ ml: 2, display: 'flex' }}>
          {exportCurlRequestString && (
            <Button
              sx={{ mr: 2 }}
              variant='contained'
              onClick={handlePrimaryButtonClick}
            >
              Copy to clipboard
            </Button>
          )}
          <Button
            disabled={!fileContent || loading}
            variant='contained'
            onClick={handleDownload}
          >
            Download results
          </Button>
        </Box>
      );
    }, [
      isUserPremium,
      isUserExpired,
      exportCurlRequestString,
      handlePrimaryButtonClick,
      handleDownload,
      fileContent,
      loading
    ]);

    const renderOutput = useMemo(() => {
      if (!isUserPremium || isUserExpired) return null;

      if (loading && isCSV) {
        return (
          <Typography sx={{ p: 2, fontSize: 14 }}>Retrieving your data, please wait...</Typography>
        );
      }

      if (!isCSV && exportCurlRequestString) {
        return (
          <Paper
            sx={{
              m: 2,
              p: 3,
              backgroundColor: '#1A224D',
              fontFamily: 'monospace',
              wordBreak: 'break-word'
            }}
          >
            {exportCurlRequestString}
          </Paper>
        );
      }

      return (
        <Box
          sx={{
            overflow: 'auto',
            whiteSpace: 'pre',
            p: 2,
            backgroundColor: '#1A224D',
            fontFamily: 'monospace',
            fontSize: 14,
            height: 541
          }}
        >
          {fileContent}
        </Box>
      );
    }, [isUserPremium, isUserExpired, loading, isCSV, exportCurlRequestString, fileContent]);

    return (
      <Box sx={{ minWidth: 470, maxWidth: 735 }}>
        {renderActionButtons}
        <Typography sx={{ p: 2, fontSize: 12 }}>
          For more information on the GX {isCSV ? 'CSV file format' : 'API'}, please visit our{' '}
          <Link
            href={`https://docs.g-x.co/#${isCSV ? 'index-keys' : 'API'}`}
            rel='noopener noreferrer'
            target='_blank'
            underline='hover'
          >
            documentation site
          </Link>
          .
        </Typography>
        {error && (
          <Typography
            color='error'
            sx={{ p: 2, fontSize: 14 }}
          >
            An error occurred while fetching data. Please try again.
          </Typography>
        )}
        {renderOutput}
      </Box>
    );
  }
);

JsonCsvPreviewScreen.displayName = 'JsonCsvPreviewScreen';

export default JsonCsvPreviewScreen;
